import { Box, Grid, Card, CardContent, Typography, Chip } from '@mui/material'
import { useSearchDataModelSchemas } from 'pages/DataModelSchema/api'
import { TranslationMap } from 'common/types/kraken-core/TranslationMap'
import ContentTypeTag from 'atoms/ContentTypeTag/ContentTypeTag'

export interface ImportModalConfigurationCardsProps {
  translationMap: TranslationMap
  viewMode?: 'simple' | 'advanced'
  onUpdate?: (translationMap: TranslationMap) => void
}

export default function ImportModalConfigurationCards({
  translationMap,
}: ImportModalConfigurationCardsProps) {
  const inputCode = translationMap?.input_data_model_code
  const outputCode = translationMap?.output_data_model_code

  // Resolve data models by code (portable, environment-independent key). The
  // list endpoint returns an empty page rather than a 404 when a code does not
  // exist in this environment, so it never trips the global error boundary.
  const { data: inputSearch } = useSearchDataModelSchemas(
    { code: inputCode, page_size: 1 },
    { enabled: Boolean(inputCode) }
  )
  const { data: outputSearch } = useSearchDataModelSchemas(
    { code: outputCode, page_size: 1 },
    { enabled: Boolean(outputCode) }
  )

  const inputSummary = inputSearch?.results?.[0]
  const outputSummary = outputSearch?.results?.[0]

  // Determine if we're using data models or content types
  const isUsingDataModels = Boolean(inputCode || outputCode)
  const isUsingContentTypes = Boolean(
    translationMap?.input_content_type || translationMap?.output_content_type
  )

  return (
    <Box>
      {/* Translation Map Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Translation Map Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{translationMap?.name || 'Not specified'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Direction
              </Typography>
              <Chip
                label={translationMap?.use_when_direction || 'Not specified'}
                color={translationMap?.use_when_direction === 'inbound' ? 'primary' : 'secondary'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Input Configuration Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Input Configuration
              </Typography>

              {isUsingDataModels && inputCode ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data Model
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {inputSummary?.description || inputCode}
                  </Typography>
                  {inputSummary?.description && (
                    <Typography variant="caption" color="text.secondary">
                      {inputCode}
                    </Typography>
                  )}
                </Box>
              ) : isUsingContentTypes && translationMap?.input_content_type ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Content Type
                  </Typography>
                  <ContentTypeTag contentType={translationMap.input_content_type} size="default" />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No input configuration specified
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Output Configuration Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Output Configuration
              </Typography>

              {isUsingDataModels && outputCode ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data Model
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {outputSummary?.description || outputCode}
                  </Typography>
                  {outputSummary?.description && (
                    <Typography variant="caption" color="text.secondary">
                      {outputCode}
                    </Typography>
                  )}
                </Box>
              ) : isUsingContentTypes && translationMap?.output_content_type ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Content Type
                  </Typography>
                  <ContentTypeTag contentType={translationMap.output_content_type} size="default" />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No output configuration specified
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
